import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { motion } from 'framer-motion';
import { tokenomicsConfig } from '@/data/tokenomicsData';

export const CirculatingSupplyBars = () => {
  const svgRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 600, height: 400 });
  
  // Responsive dimensions
  useEffect(() => {
    const handleResize = () => {
      if (svgRef.current?.parentElement) {
        const parent = svgRef.current.parentElement;
        setDimensions({ 
          width: Math.min(parent.clientWidth - 40, 700), 
          height: 400 
        });
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Separate unlocked and locked allocations
  const unlockedAllocations = tokenomicsConfig.allocations.filter(a => a.unlockedAtTGE);
  const lockedAllocations = tokenomicsConfig.allocations.filter(a => !a.unlockedAtTGE);
  
  // D3 Horizontal Bar Chart
  useEffect(() => {
    if (!svgRef.current) return;
    
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    
    const { width, height } = dimensions;
    const margin = { top: 30, right: 100, bottom: 30, left: 150 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;
    
    const allAllocations = [...unlockedAllocations, ...lockedAllocations];
    
    const g = svg
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);
    
    // Scales
    const yScale = d3.scaleBand()
      .domain(allAllocations.map(d => d.id))
      .range([0, innerHeight])
      .padding(0.25);
    
    const xScale = d3.scaleLinear()
      .domain([0, 25])
      .range([0, innerWidth]);
    
    // Glow filter
    const defs = svg.append('defs');
    const filter = defs.append('filter')
      .attr('id', 'hBarGlow')
      .attr('x', '-20%')
      .attr('y', '-20%')
      .attr('width', '140%')
      .attr('height', '140%');
    
    filter.append('feGaussianBlur')
      .attr('stdDeviation', '2')
      .attr('result', 'coloredBlur');
    
    const feMerge = filter.append('feMerge');
    feMerge.append('feMergeNode').attr('in', 'coloredBlur');
    feMerge.append('feMergeNode').attr('in', 'SourceGraphic');
    
    // Background bars
    g.selectAll('.bg-bar')
      .data(allAllocations)
      .enter()
      .append('rect')
      .attr('class', 'bg-bar')
      .attr('x', 0)
      .attr('y', d => yScale(d.id))
      .attr('width', innerWidth)
      .attr('height', yScale.bandwidth())
      .attr('fill', 'hsl(215, 20%, 15%)')
      .attr('rx', 4);
    
    // Animated bars
    g.selectAll('.bar')
      .data(allAllocations)
      .enter()
      .append('rect')
      .attr('class', 'bar')
      .attr('x', 0)
      .attr('y', d => yScale(d.id))
      .attr('width', 0)
      .attr('height', yScale.bandwidth())
      .attr('fill', d => d.color)
      .attr('rx', 4)
      .style('filter', 'url(#hBarGlow)')
      .transition()
      .duration(1000)
      .delay((d, i) => i * 80)
      .ease(d3.easeBackOut.overshoot(0.5))
      .attr('width', d => xScale(d.percentage));
    
    // Labels on left
    g.selectAll('.label')
      .data(allAllocations)
      .enter()
      .append('text')
      .attr('class', 'label')
      .attr('x', -10)
      .attr('y', d => yScale(d.id) + yScale.bandwidth() / 2)
      .attr('dy', '0.35em')
      .attr('text-anchor', 'end')
      .style('font-size', '11px')
      .style('fill', 'hsl(210, 15%, 70%)')
      .text(d => d.name);
    
    // Percentage labels on right
    g.selectAll('.percentage')
      .data(allAllocations)
      .enter()
      .append('text')
      .attr('class', 'percentage')
      .attr('x', d => xScale(d.percentage) + 8)
      .attr('y', d => yScale(d.id) + yScale.bandwidth() / 2)
      .attr('dy', '0.35em')
      .style('font-size', '12px')
      .style('font-weight', '600')
      .style('fill', 'hsl(210, 20%, 98%)')
      .style('opacity', 0)
      .text(d => `${d.percentage}%`)
      .transition()
      .duration(1000)
      .delay((d, i) => i * 80 + 500)
      .style('opacity', 1);
    
    // Status indicators
    g.selectAll('.status')
      .data(allAllocations)
      .enter()
      .append('text')
      .attr('class', 'status')
      .attr('x', innerWidth + 50)
      .attr('y', d => yScale(d.id) + yScale.bandwidth() / 2)
      .attr('dy', '0.35em')
      .attr('text-anchor', 'middle')
      .style('font-size', '9px')
      .style('font-weight', '500')
      .style('fill', d => d.unlockedAtTGE ? 'hsl(142, 60%, 50%)' : 'hsl(45, 85%, 55%)')
      .text(d => d.unlockedAtTGE ? 'UNLOCKED' : 'LOCKED');
    
    // Divider line between unlocked and locked
    const dividerY = yScale(lockedAllocations[0]?.id) - yScale.bandwidth() * 0.5;
    
    if (dividerY > 0) {
      g.append('line')
        .attr('x1', -margin.left + 20)
        .attr('x2', innerWidth + margin.right - 20)
        .attr('y1', dividerY)
        .attr('y2', dividerY)
        .attr('stroke', 'hsl(215, 20%, 25%)')
        .attr('stroke-dasharray', '4,4');
      
      // Section labels
      g.append('text')
        .attr('x', innerWidth / 2)
        .attr('y', -15)
        .attr('text-anchor', 'middle')
        .style('font-size', '10px')
        .style('fill', 'hsl(142, 60%, 50%)')
        .style('font-weight', '600')
        .text('CIRCULATING AT TGE (80%)');
      
      g.append('text')
        .attr('x', innerWidth / 2)
        .attr('y', dividerY + 15)
        .attr('text-anchor', 'middle')
        .style('font-size', '10px')
        .style('fill', 'hsl(45, 85%, 55%)')
        .style('font-weight', '600')
        .text('LOCKED AT TGE (20%)');
    }
    
  }, [dimensions, unlockedAllocations, lockedAllocations]);
  
  return (
    <div className="space-y-6">
      {/* Main percentage display */}
      <motion.div 
        initial={{ scale: 0.9, opacity: 0 }}
        whileInView={{ scale: 1, opacity: 1 }}
        viewport={{ once: true }}
        className="text-center"
      >
        <p className="text-muted-foreground mb-2">Initial Float at TGE</p>
        <div className="relative inline-block">
          <span className="text-7xl md:text-8xl font-bold text-primary text-glow">
            80%
          </span>
          <div className="absolute -inset-4 bg-primary/10 blur-3xl rounded-full -z-10" />
        </div>
      </motion.div>
      
      {/* Chart */}
      <div className="glass rounded-xl p-4 md:p-6 overflow-x-auto">
        <svg ref={svgRef} className="w-full min-w-[500px]" />
      </div>
      
      {/* Summary */}
      <div className="grid grid-cols-2 gap-4">
        <div className="glass rounded-xl p-4 border-l-4 border-success">
          <h4 className="font-heading font-semibold text-success mb-2">Circulating at TGE</h4>
          <p className="text-3xl font-bold text-foreground">80%</p>
          <p className="text-sm text-muted-foreground mt-1">
            Liquidity, Community, Marketing, Treasury, R&D, Governance
          </p>
        </div>
        <div className="glass rounded-xl p-4 border-l-4 border-warning">
          <h4 className="font-heading font-semibold text-warning mb-2">Locked at TGE</h4>
          <p className="text-3xl font-bold text-foreground">20%</p>
          <p className="text-sm text-muted-foreground mt-1">
            Investors (15%) + Team (5%) with vesting schedules
          </p>
        </div>
      </div>
    </div>
  );
};

export default CirculatingSupplyBars;
